// Key figures of creations ("facts") and mode rule descriptions. The creations package and the core compute them in
// English, and the server stores facts as computed, so older creations still carry French values. Some values are raw
// persisted ids (weather, finish, rim depth, game mechanics), which are French because they are stored data.
import type { ErrorPattern } from './content-fr-errors.js';

/** English text and persisted ids → French. */
export const factValuesFr: Record<string, string> = {
  yes: 'oui', no: 'non', open: 'libre', compatible: 'compatible', forest: 'forêt', hedgerows: 'bocage',
  jersey: 'maillot', frame: 'cadre', helmet: 'casque', wheels: 'roues',
  yourself: 'soi', 'the rider ahead': 'le coureur devant', 'the leader': 'le leader', 'everyone ahead': 'tous ceux devant',
  'everyone else': 'tous les autres', 'whoever rides over it': 'celui qui roule dessus', instant: 'immédiat', trap: 'piège', missile: 'missile',
  clair: 'clair', pluie: 'pluie', neige: 'neige', brume: 'brume', orage: 'orage',
  brillant: 'brillant', mat: 'mat', metal: 'métal', chrome: 'chrome', nacre: 'nacré', carbone: 'carbone',
  bas: 'bas', moyen: 'moyen', haut: 'haut', lenticulaire: 'lenticulaire',
  moteur: 'moteur', batterie: 'batterie', zone: 'zone', explosion: 'explosion', reserve: 'réserve', rythme: 'rythme', charge: 'charge',
};

/** Legacy French values and persisted ids → English. */
export const factValuesEn: Record<string, string> = {
  oui: 'yes', non: 'no', libre: 'open', forêt: 'forest', bocage: 'hedgerows', dégagé: 'open',
  maillot: 'jersey', cadre: 'frame', casque: 'helmet', roues: 'wheels',
  soi: 'yourself', 'le coureur devant': 'the rider ahead', 'le leader': 'the leader', 'tous ceux devant': 'everyone ahead',
  'tous les autres': 'everyone else', 'celui qui roule dessus': 'whoever rides over it', immédiat: 'instant', piège: 'trap',
  clair: 'clear', pluie: 'rain', neige: 'snow', brume: 'mist', orage: 'storm',
  brillant: 'gloss', mat: 'matte', metal: 'metallic', nacre: 'pearl', carbone: 'carbon',
  bas: 'low', moyen: 'mid', haut: 'deep', lenticulaire: 'disc',
  moteur: 'engine', batterie: 'battery', explosion: 'blast', reserve: 'reserve', rythme: 'rhythm',
};

const plural = (n: string, word: string): string => `${n} ${word}${n === '1' ? '' : 's'}`;

/** Mode rule descriptions (`describeRules` in the core) → French. */
export const rulePatternsFr: ErrorPattern[] = [
  [/^First after (\d+) laps?$/, m => `Premier après ${plural(m[1], 'tour')}`],
  [/^Last rider standing wins$/, () => 'Le dernier coureur en lice gagne'],
  [/^(\d+)-minute game$/, m => `Partie de ${m[1]} min`],
  [/^Open game, no finish line$/, () => 'Partie ouverte, sans arrivée'],
  [/^Time limit: (\d+) min$/, m => `Limite de temps : ${m[1]} min`],
  [/^The last rider is eliminated each time the leader completes a lap$/, () => 'Le dernier est éliminé à chaque tour du leader'],
  [/^Items: (\d+) allowed$/, m => `Objets : ${m[1]} autorisés`],
  [/^Items on$/, () => 'Objets activés'],
  [/^No items$/, () => 'Sans objets'],
  [/^Arena: boxes closer together$/, () => 'Arène : caisses rapprochées'],
  [/^Drafting off$/, () => 'Aspiration désactivée'],
  [/^Bots ride as a group at your pace$/, () => 'Bots en groupe à ton allure'],
  [/^Bots matched to your W\/kg$/, () => 'Bots calibrés sur tes W/kg'],
  [/^Bots never wait$/, () => 'Bots sans attente'],
  [/^Ranked by number of hits$/, () => 'Classement au nombre de touches'],
  // the item names after the colon are player text: kept as they are
  [/^Created items added : (.*)$/s, m => `Objets créés en plus : ${m[1]}`],
  [/^Created items only : (.*)$/s, m => `Uniquement des objets créés : ${m[1]}`],
  [/^Against your ghost$/, () => 'Contre ton fantôme'],
  // team modes (core describeRules)
  [/^Teams of (\d+) by club$/, m => `Équipes de ${m[1]} par club`],
  [/^Balanced teams of (\d+)$/, m => `Équipes équilibrées de ${m[1]}`],
  [/^Team time on its (first|second|third|\d+th) rider$/, m => `Temps d’équipe sur son ${({ first: 'premier', second: 'deuxième', third: 'troisième' } as Record<string, string>)[m[1]] ?? m[1].replace('th', 'e')} coureur`],
  [/^Team ranked by its best rider$/, () => 'Équipe classée par son meilleur coureur'],
  [/^Team ranked by the sum of its riders’ places$/, () => 'Équipe classée à la somme des places de ses coureurs'],
  [/^Teams start (.+) apart$/, m => `Départs des équipes toutes les ${m[1]}`],
  [/^Drafting behind teammates only$/, () => 'Aspiration derrière les coéquipiers seulement'],
];
